import { z } from 'zod'

export const DeliverableStatusEnum = {
  PLANNED: 'PLANNED',
  ACTIVE: 'ACTIVE',
  ACCEPTED: 'ACCEPTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  ARCHIVED: 'ARCHIVED',
} as const

export const DeliverableBillingTypeEnum = {
  TIME_AND_MATERIALS: 'TIME_AND_MATERIALS',
  FIXED_FEE: 'FIXED_FEE',
  BLENDED: 'BLENDED',
  RETAINER: 'RETAINER',
  SUBSCRIPTION: 'SUBSCRIPTION',
} as const

export const DeliverableStatusSchema = z.enum([
  DeliverableStatusEnum.PLANNED,
  DeliverableStatusEnum.ACTIVE,
  DeliverableStatusEnum.ACCEPTED,
  DeliverableStatusEnum.COMPLETED,
  DeliverableStatusEnum.CANCELLED,
  DeliverableStatusEnum.ARCHIVED,
])

export const DeliverableBillingTypeSchema = z.enum([
  DeliverableBillingTypeEnum.TIME_AND_MATERIALS,
  DeliverableBillingTypeEnum.FIXED_FEE,
  DeliverableBillingTypeEnum.BLENDED,
  DeliverableBillingTypeEnum.RETAINER,
  DeliverableBillingTypeEnum.SUBSCRIPTION,
])

export type DeliverableBillingType = z.infer<typeof DeliverableBillingTypeSchema>

export const DeliverableTeamSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

export const DeliverableProjectSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable().optional(),
})

export const DeliverableContractSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  contractNumber: z.string().nullable().optional(),
})

export const DeliverableSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  contractId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  ownerTeamId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: DeliverableStatusSchema.default(DeliverableStatusEnum.ACTIVE),
  billingType: DeliverableBillingTypeSchema.default(
    DeliverableBillingTypeEnum.TIME_AND_MATERIALS,
  ),
  allocatedValue: z.union([z.number(), z.string()]).nullable().optional(),
  blendedRate: z.union([z.number(), z.string()]).nullable().optional(),
  discountPercent: z.union([z.number(), z.string()]).nullable().optional(),
  currency: z.string().default('USD'),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  acceptedAt: z.coerce.date().nullable().optional(),
  sortOrder: z.number().int().default(0),
  settings: z.unknown().default({}),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  ownerTeam: DeliverableTeamSummarySchema.nullable().optional(),
  project: DeliverableProjectSummarySchema.nullable().optional(),
  contract: DeliverableContractSummarySchema.optional(),
})

export type Deliverable = z.infer<typeof DeliverableSchema>

export const CreateDeliverableSchema = z.object({
  entityId: z.string().uuid(),
  contractId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  ownerTeamId: z.string().uuid().nullable().optional(),
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().nullable().optional(),
  status: DeliverableStatusSchema.optional(),
  billingType: DeliverableBillingTypeSchema.optional(),
  allocatedValue: z.union([z.number(), z.string()]).nullable().optional(),
  blendedRate: z.union([z.number(), z.string()]).nullable().optional(),
  discountPercent: z.union([z.number(), z.string()]).nullable().optional(),
  currency: z.string().trim().length(3).optional(),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  acceptedAt: z.coerce.date().nullable().optional(),
  sortOrder: z.number().int().optional(),
  settings: z.unknown().optional(),
})

export type CreateDeliverable = z.infer<typeof CreateDeliverableSchema>

export const UpdateDeliverableSchema = CreateDeliverableSchema.omit({
  entityId: true,
  contractId: true,
}).partial()

export type UpdateDeliverable = z.infer<typeof UpdateDeliverableSchema>
